import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  FormFieldError,
  FormFieldLabel,
  Input,
  RichTextEditor,
} from '@eureca/ui';

export interface FaqDialogValue {
  question: string;
  answer: string;
}

export interface FaqDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, dialog is in edit mode and pre-fills the fields. */
  initialValue?: FaqDialogValue | null;
  onSubmit: (value: FaqDialogValue) => void;
}

const EMPTY: FaqDialogValue = { question: '', answer: '' };

export function FaqDialog({ open, onOpenChange, initialValue, onSubmit }: FaqDialogProps) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  // EU-164 FR-099: campos só mostram erro depois que o usuário interagiu
  // (blur ou submit). Evita banner vermelho na abertura do modal.
  const [questionTouched, setQuestionTouched] = useState(false);
  const [answerTouched, setAnswerTouched] = useState(false);

  useEffect(() => {
    if (open) {
      const initial = initialValue ?? EMPTY;
      setQuestion(initial.question);
      setAnswer(initial.answer);
      setQuestionTouched(false);
      setAnswerTouched(false);
    }
  }, [open, initialValue]);

  const isEditing = Boolean(initialValue);
  const questionError = question.trim().length === 0 ? 'Informe a pergunta' : '';
  const answerError = answer.trim().length === 0 ? 'Informe a resposta' : '';
  const canSubmit = !questionError && !answerError;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) {
      setQuestionTouched(true);
      setAnswerTouched(true);
      return;
    }
    onSubmit({ question: question.trim(), answer: answer.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-0 lg:w-full lg:max-w-[480px] lg:rounded-xl"
      >
        <DialogHeader className="gap-1.5 px-6 pt-6">
          <DialogTitle className="text-lg font-semibold leading-none tracking-[-0.025em] text-foreground">
            {isEditing ? 'Editar pergunta' : 'Adicionar pergunta'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-5 px-6 pt-3 pb-8">
            <FormField error={Boolean(questionTouched && questionError)}>
              <FormFieldLabel className="text-foreground">Pergunta</FormFieldLabel>
              <Input
                autoFocus
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onBlur={() => setQuestionTouched(true)}
                placeholder="Ex.: Qual o horário de trabalho?"
                variant={questionTouched && questionError ? 'destructive' : 'default'}
                data-testid="faq-dialog-question"
              />
              {questionTouched && questionError ? (
                <FormFieldError>{questionError}</FormFieldError>
              ) : null}
            </FormField>
            <FormField error={Boolean(answerTouched && answerError)}>
              <FormFieldLabel className="text-foreground">Resposta</FormFieldLabel>
              <RichTextEditor
                value={answer}
                onChange={(html) => setAnswer(html)}
                onBlur={() => setAnswerTouched(true)}
                data-testid="faq-dialog-answer"
              />
              {answerTouched && answerError ? <FormFieldError>{answerError}</FormFieldError> : null}
            </FormField>
          </div>
          <DialogFooter className="px-6 pb-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit} data-testid="faq-dialog-submit">
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
